import { FC, useEffect, useMemo, useState } from "react";
import ShipmentForm, { minDeclaredValue } from "./ShipmentForm";
import {
  alertService,
  getAccount,
  getAllShipmentContentInvoice,
  getFileDO,
  getService,
  getShipment,
  loaderService,
  verifyAccountBlocked,
} from "../../services";
import { ExclamationTriangleIcon, PlusIcon } from "@heroicons/react/20/solid";
import {
  clearShipmentCreate,
  setShipmentCreateDrafts,
} from "../../store/slices";
import {
  DeliveryType,
  PaymentMode,
  ShipmentInterface,
  ShipmentService,
  ShipmentStatus,
  Undefinable,
} from "../../interfaces";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import {
  LinkText,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons";
import { isBatchEligible, isSameBatch, validateAccount } from "../../utils";
import { BlockedReason } from "../../components/Account/AccountBlocked";
import Modal from "../../components/Modal";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";

interface ShipmentFormListProps {
  /**
   * On submit callback
   */
  onSubmit?: () => void;
  invoices: File[][];
  setInvoices: React.Dispatch<React.SetStateAction<File[][]>>;
}
const ShipmentFormList: FC<ShipmentFormListProps> = ({
  invoices,
  setInvoices,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);
  const countries = useAppSelector((state) => state.inmutable.countries);
  const maxDiscount = useAppSelector((state) => state.inmutable.maxDiscount);
  const shipmentDrafts = useAppSelector(
    (state) => state.shipmentCreate.shipmentDrafts
  );
  const rangeMinDeclareList = useAppSelector(
    (state) => state.inmutable.rangeDeclareList
  );
  const highDeclaredValue = useAppSelector(
    (state) => state.inmutable.highDeclaredValue
  )?.valueGeneral;

  const [element, setElement] = useState(0);
  const [openDiscardAll, setOpenDiscardAll] = useState(false);
  const [errors, setErrors] = useState<Map<number, Undefinable<string>>>(
    new Map()
  );
  const [international, setInternational] = useState(
    !!shipmentDrafts[0].isInternational
  );

  // A preshipment lote is anchored by a draft loaded from a pre-guía. Every
  // guía in it must also come from a pre-guía (isolated case).
  const isPreshipmentBatch = !!shipmentDrafts[0]?.preShipmentHeaderID;

  // D1: in a preshipment lote, don't let the agent pile up empty drafts —
  // require the last added guía to have loaded its pre-guía before adding more.
  const lastDraftNeedsPreshipment = useMemo(() => {
    return (
      isPreshipmentBatch &&
      shipmentDrafts.length > 0 &&
      !shipmentDrafts[shipmentDrafts.length - 1]?.preShipmentHeaderID
    );
  }, [isPreshipmentBatch, shipmentDrafts]);

  // A lote can grow when its anchor draft is batch-eligible: a manual batch in
  // an enabled payment mode (Contado, Crédito Taquilla, COD, Crédito
  // Corporativo) or a preshipment batch (any matching mode). See isBatchEligible.
  const isCreationDisabled = useMemo(() => {
    return (
      shipmentDrafts.length > 0 &&
      (!isBatchEligible(shipmentDrafts[0]) || lastDraftNeedsPreshipment)
    );
  }, [shipmentDrafts, lastDraftNeedsPreshipment]);

  useEffect(() => {
    const loadInvoices = async () => {
      const invoicesList: File[][] = [];

      for (let index = 0; index < shipmentDrafts.length; index++) {
        const draft = shipmentDrafts[index];
        const shipmentHeaderID = draft?.id;

        // If there's no shipmentHeaderID just copy existing invoices for this
        // index
        if (!shipmentHeaderID) {
          invoicesList.push(invoices[index] || []);
          continue;
        }

        const response = await getAllShipmentContentInvoice(shipmentHeaderID);

        if (response.didError || !response.model) {
          invoicesList.push(invoices[index] || []);
          continue;
        }

        const files: File[] = [];
        for (let i = 0; i < response.model.length; i++) {
          const invoice = response.model[i];
          const file = await getFileDO(
            invoice.attachmentID,
            `Adjunto ${i + 1}`
          );
          if (file.didError || !file.model) {
            // If a single file fails, skip it and continue with others
            alertService.warn(
              `Hubo un error al descargar el archivo ${invoice.attachmentID} para la guía ${shipmentHeaderID}`
            );
            continue;
          }
          files.push(file.model);
        }

        invoicesList.push(files);
      }

      setInvoices(invoicesList);
    };

    loadInvoices();
    // Re-run when number of drafts changes
    // We only want to re-run this loader when the number of drafts changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentDrafts.length]);

  // DD1: tell the agent WHY "Añadir nueva guía" is disabled instead of leaving
  // them staring at a greyed button. Plain text (not color) so it reaches
  // screen-reader and colorblind users too.
  const creationDisabledReason = useMemo(() => {
    if (!isCreationDisabled) return undefined;
    if (lastDraftNeedsPreshipment) {
      return "Carga la pre-guía de la guía actual antes de añadir otra.";
    }
    return "Disponible solo para Contado, Crédito Taquilla, COD y Crédito Corporativo.";
  }, [isCreationDisabled, lastDraftNeedsPreshipment]);

  const Forms = useMemo(() => {
    const nDrafts = shipmentDrafts.length;

    return Array(nDrafts)
      .fill(null)
      .map((_, index) => {
        return (
          <div key={index} id={`shipment-form-index-${index}`}>
            <ShipmentForm
              index={index}
              error={errors.get(index)}
              active={index === element}
              disablePaymentMode={nDrafts > 1}
              shipperIsEditable={shipmentDrafts.length === 1}
              cleanable={index > 0 && index === shipmentDrafts.length - 1}
              deleteable={index > 0 || !!shipmentDrafts[index].shipmentNumber}
              invoices={invoices}
              setInvoices={setInvoices}
              onDelete={() => {
                if (index === element) {
                  setElement(-1);
                } else if (index < element) {
                  setElement(element - 1);
                }
              }}
              onArrowClick={() => {
                setElement(index === element ? -1 : index);
                setErrors((errors) => new Map(errors.set(index, undefined)));
              }}
              setInternational={setInternational}
            />
          </div>
        );
      });
  }, [shipmentDrafts.length, errors, element, invoices]);

  const verifyShipments = async () => {
    let error = false;
    let scroll = false;

    for (let index = 0; index < shipmentDrafts.length; index++) {
      const shipment = shipmentDrafts[index];
      const errorString = await draftIsValid(shipment, index);

      if (!!errorString && errorString.trim().length > 0) {
        error = true;
        setElement(-1);
        setErrors((errors) => new Map(errors.set(index, errorString)));

        if (!scroll) {
          const element = document.getElementById(
            `shipment-form-index-${index}`
          );
          if (element) {
            const elementRect = element.getBoundingClientRect();
            window.scrollTo({
              top: elementRect.top,
              behavior: "smooth",
            });
            scroll = true;
          }
        }
      }
    }

    return error;
  };

  const handleSubmit = async () => {
    loaderService.start();

    // WS2: a preshipment lote must be fully sourced from pre-guías — block an
    // empty trailing draft that was added but never loaded.
    if (
      isPreshipmentBatch &&
      !shipmentDrafts.every((draft) => !!draft.preShipmentHeaderID)
    ) {
      loaderService.stop();
      alertService.error(
        "Todas las guías del lote deben provenir de una pre-guía."
      );
      return;
    }

    // WS2: each guía in a preshipment lote must come from a DISTINCT pre-guía —
    // reject duplicates (safety net for the per-load check).
    if (isPreshipmentBatch) {
      const preShipmentIDs = shipmentDrafts.map((d) => d.preShipmentHeaderID);
      if (new Set(preShipmentIDs).size !== preShipmentIDs.length) {
        loaderService.stop();
        alertService.error(
          "No se puede repetir la misma pre-guía en el lote."
        );
        return;
      }
    }

    // Final safety net for the shared declaración jurada: every guía in a lote
    // must share the same remitente + modalidad as the anchor. Per-load checks
    // skip draft[0], so a re-loaded anchor (Quitar pre-guía) is caught here.
    if (
      shipmentDrafts.length > 1 &&
      !shipmentDrafts.every((draft) => isSameBatch(draft, shipmentDrafts[0]))
    ) {
      loaderService.stop();
      alertService.error(
        "Todas las guías del lote deben tener el mismo remitente y modalidad de pago."
      );
      return;
    }

    const error = await verifyShipments();

    if (!error) {
      onSubmit();
    }
    loaderService.stop();
  };

  const draftIsValid = async (
    draft: Partial<ShipmentInterface>,
    index: number
  ) => {
    let msg = "";
    let valid = true;

    if (!draft.shipper) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Remitente";
      valid = false;
    }
    if (!draft.consignee) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Destinatario";
      valid = false;
    }
    if (!draft.paymentMode) {
      msg +=
        (valid ? "Faltan los siguientes campos: " : ", ") + "Modalidad de Pago";
      valid = false;
    }
    if (!draft.buSource) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Origen";
      valid = false;
    }
    if (!draft.consigneeAddress) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Destino";
      valid = false;
    }
    if (!draft.pieces) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Piezas";
      valid = false;
    }
    if (!draft.pieces?.length) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Piezas";
      valid = false;
    }
    if (
      false && // hidden until backend is ready
      draft.declaredValue &&
      highDeclaredValue &&
      +draft.declaredValue! >= +highDeclaredValue!
    ) {
      if (!draft.invoiceNumber) {
        msg +=
          (valid ? "Faltan los siguientes campos: " : ", ") +
          "Número de factura";
        valid = false;
      }
      if (!invoices[index] || invoices[index].length === 0) {
        msg +=
          (valid ? "Faltan los siguientes campos: " : ", ") +
          "Adjunto de la factura";
        valid = false;
      }
    }

    if (!draft.items) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Servicios";
      valid = false;
    }
    // For international shipments the "Dirección" field (consigneeAddress.reference)
    // is mandatory. Key off the per-draft flag and check the field's actual value
    // (trimmed) so undefined/whitespace-only input is also rejected.
    if (draft.isInternational && !draft.consigneeAddress?.reference?.trim()) {
      msg += (valid ? "Faltan los siguientes campos: " : ", ") + "Dirección";
      valid = false;
    }
    if (
      draft.pieces?.some(
        (p) =>
          !p.weight ||
          p.weight <= 0 ||
          !p.height ||
          p.height <= 0 ||
          !p.length ||
          p.length <= 0 ||
          !p.width ||
          p.width <= 0
      )
    ) {
      msg +=
        (valid ? "" : ". ") +
        "Revise que todas las piezas tengan sus medidas y peso";
      valid = false;
    }

    if (
      valid &&
      draft.items &&
      (!draft.items.some((item) => item.isItemBase) ||
        (draft.items
          .filter((item) => item.isItemBase)
          .some((item) => !item.rate.value) &&
          draft.paymentMode !== PaymentMode.CREDIT))
    ) {
      const error =
        "No se ha logrado obtener el cálculo de tarifas. Por favor espere unos segundos o recargue para intentar de nuevo.";
      if (valid) msg = error;
      else msg += `.\n${error}`;
      valid = false;
    }

    if (!!draft.discount && +draft.discount > maxDiscount) {
      if (valid) msg = `El descuento no puede ser mayor al ${maxDiscount}%`;
      else msg += `.\nEl descuento no puede ser mayor al ${maxDiscount}%`;

      valid = false;
    }

    if (draft.shipper) {
      let shipperValid = true;
      let validations = await validateAccount(draft.shipper, countries);
      if (validations.error) {
        msg +=
          (valid ? "" : "\n ") + "Remitente inválido: " + validations.message;
        valid = false;
        shipperValid = false;
      }

      // Verify that the account is not blocked
      const isBlocked = await verifyAccountBlocked(
        draft.shipper.id,
        draft.service!,
        draft.paymentMode!,
        draft.buSource!.code
      );

      if (isBlocked.didError || isBlocked.model === null) {
        alertService.warn(
          "Hubo un error al verificar si el remitente está bloqueado."
        );
      } else if (isBlocked.model && isBlocked.model.length > 0) {
        const blockedReason = isBlocked.model[0].blockedReasonID;

        if (blockedReason === BlockedReason.OTHER) {
          msg +=
            (valid ? "" : "\n ") +
            (shipperValid ? "Remitente bloqueado: E" : ", e") +
            "l cliente está bloqueado para recibir o realizar envíos";
          valid = false;
          shipperValid = false;
        } else {
          msg +=
            (valid ? "" : "\n ") +
            (shipperValid ? "Remitente bloqueado: E" : ", e") +
            "l cliente no puede realizar envíos de crédito hasta que no cancele su deuda pendiente";
          valid = false;
          shipperValid = false;
        }
      }
    }
    if (draft.consignee) {
      let consigneeValid = true;
      let validations = await validateAccount(draft.consignee, countries);
      if (validations.error) {
        msg +=
          (valid ? "" : "\n ") +
          "Destinatario inválido: " +
          validations.message;
        valid = false;
        consigneeValid = false;
      }

      // Verify that the account is not blocked
      const isBlocked = await verifyAccountBlocked(
        draft.consignee.id,
        draft.service!,
        draft.paymentMode!,
        draft.buSource!.code
      );

      if (isBlocked.didError || isBlocked.model === null) {
        alertService.warn(
          "Hubo un error al verificar si el destinatario está bloqueado."
        );
      } else if (isBlocked.model && isBlocked.model.length > 0) {
        const blockedReason = isBlocked.model[0].blockedReasonID;

        if (blockedReason === BlockedReason.OTHER) {
          msg +=
            (valid ? "" : "\n ") +
            (consigneeValid ? "Destinatario bloqueado: E" : ", e") +
            "l cliente está bloqueado para recibir o realizar envíos";
          valid = false;
          consigneeValid = false;
        } else {
          msg +=
            (valid ? "" : "\n ") +
            (consigneeValid ? "Destinatario bloqueado: E" : ", e") +
            "l cliente no puede realizar envíos de crédito hasta que no cancele su deuda pendiente";
          valid = false;
          consigneeValid = false;
        }
      }
    }

    if (
      !draft.boxAccount &&
      draft.service !== ShipmentService.DOCUMENT &&
      draft.paymentMode !== PaymentMode.CREDIT
    ) {
      const weight = draft.totalChargedWeight ?? 0;
      const minVal = minDeclaredValue(
        weight,
        rangeMinDeclareList,
        draft.service
      );

      if (
        parseFloat(draft.declaredValue ? draft.declaredValue : "0") < minVal &&
        !international
      ) {
        const error = `El valor declarado no puede ser menor a ${minVal.toFixed(
          2
        )}$`;
        msg = valid ? error : msg + "\n" + error;
        valid = false;
      }
    }

    if (draft.pieces?.length && draft.service) {
      const service = await getService(draft.service);

      if (!service.didError && service.model) {
        const weight = draft.totalChargedWeight;

        if ((weight ?? 0) < (service.model.minWeight ?? 0)) {
          const error = `El peso total de las piezas no puede ser menor a ${service.model.minWeight} kg`;
          msg = valid ? error : msg + "\n" + error;
          valid = false;
        } else if ((weight ?? 0) > (service.model.maxWeight ?? Infinity)) {
          const error = `El peso total de las piezas no puede ser mayor a ${service.model.maxWeight} kg`;
          msg = valid ? error : msg + "\n" + error;
          valid = false;
        }
      }
    }

    if (
      draft.paymentMode === PaymentMode.CREDIT &&
      !!draft.accountBillTo?.maxWeightAllowed &&
      (draft.totalChargedWeight ?? 0) > draft.accountBillTo?.maxWeightAllowed
    ) {
      const error = `El peso total de las piezas no puede ser mayor a ${draft.accountBillTo.maxWeightAllowed.toFixed(
        2
      )} kg`;
      msg = valid ? error : msg + "\n" + error;
      valid = false;
    }

    return valid === false ? msg : undefined;
  };

  const handleDiscardAll = async () => {
    setOpenDiscardAll(false);
    dispatch(clearShipmentCreate());
    const filteredShipmentDraft = shipmentDrafts.filter(
      (s) => !!s.shipmentNumber
    );
    if (filteredShipmentDraft.length > 0) {
      navigate(`/shipments/${filteredShipmentDraft[0].shipmentNumber}`);
    }
  };

  const addNewShipment = async () => {
    const error = await verifyShipments();
    if (error) return;

    setElement(shipmentDrafts.length);
    setInvoices((invoices) => [...invoices, []]);

    // WS2: in a preshipment lote the new guía is filled (and validated against
    // the lote) by loading its own pre-guía, so start it EMPTY — don't copy the
    // shipper/mode/billing of draft[0]. Manual batches keep copying the anchor.
    const newDraft: Partial<ShipmentInterface> = isPreshipmentBatch
      ? {
          pieces: [],
          items: [],
          buSource: user.businessUnit,
          status: ShipmentStatus.DRAFT,
          service: ShipmentService.STANDARD,
          deliveryType: DeliveryType.AT_OFFICE,
        }
      : {
          shipper: shipmentDrafts[0].shipper,
          pieces: [],
          items: [],
          paymentMode: shipmentDrafts[0].paymentMode,
          buSource: user.businessUnit,
          status: ShipmentStatus.DRAFT,
          deliveryType: shipmentDrafts[0].deliveryType,
          accountBillTo: shipmentDrafts[0].accountBillTo,
          // Carry the billing site so added Crédito Corporativo drafts keep the
          // accountSiteID that rate/tier lookups depend on.
          accountSiteID: shipmentDrafts[0].accountSiteID,
        };

    dispatch(setShipmentCreateDrafts([...shipmentDrafts, newDraft]));
  };

  const addExistingShipment = async (shipmentNumber: string) => {
    const error = await verifyShipments();
    if (error) return;

    if (
      !!shipmentNumber &&
      shipmentDrafts.some((s) => s.shipmentNumber === shipmentNumber)
    ) {
      alertService.error(`La guía ${shipmentNumber} ya está añadida.`);
      return;
    }

    const shipment = await getShipment(shipmentNumber);

    if (!shipment) {
      alertService.error(`No se pudo obtener la guía ${shipmentNumber}.`);
      return;
    }

    if (!isSameBatch(shipment, shipmentDrafts[0])) {
      alertService.error(
        `La guía ${shipmentNumber} no tiene la misma tienda, remitente o modalidad de pago.`
      );
      return;
    }

    setElement(shipmentDrafts.length);
    dispatch(setShipmentCreateDrafts([...shipmentDrafts, shipment]));
  };

  useEffect(() => {
    const updateAccount = async (id: string) => {
      return await getAccount(id);
    };

    const updateAccounts = async () => {
      const drafts = await Promise.all(
        shipmentDrafts.map(async (draft) => {
          const shipper =
            (await updateAccount(draft.shipper?.id ?? "")) ?? undefined;
          const consignee =
            (await updateAccount(draft.consignee?.id ?? "")) ?? undefined;
          return {
            ...draft,
            shipper,
            consignee,
          };
        })
      );

      dispatch(setShipmentCreateDrafts(drafts));
    };

    updateAccounts();

    // Shippers and consignees only need to be updated once when rendering this
    // component.Do not add shipmentDrafts to the dependency list to avoid an
    // infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setErrors(new Map());
  }, [shipmentDrafts]);

  return (
    <div className="flex flex-1 flex-col">
      {/* Shipments */}
      <div className="flex flex-1 flex-col gap-4">
        {shipmentDrafts.length === 0 && (
          <div
            className="flex flex-1 items-center justify-center"
            style={{ minHeight: "10rem" }}
          >
            <p className="text-gray-500 leading-5">No hay guías registradas</p>
          </div>
        )}

        {Forms}
      </div>

      {/* Footer */}
      <div className="flex flex-1 justify-between gap-4 items-center mt-8">
        <div>
          <LinkText
            className={classNames(
              !shipmentDrafts.some((s) => !!s.shipmentNumber) && "hidden"
            )}
            text="Descartar cambios"
            onClick={() => setOpenDiscardAll(true)}
          />
        </div>

        <div className="flex items-center gap-4">
          <div className="flex flex-col items-end gap-1">
            <SecondaryButton
              className="flex gap-1 w-44"
              disabled={isCreationDisabled}
              onClick={addNewShipment}
            >
              <PlusIcon className="h-5 w-5 flex-none" aria-hidden="true" />
              Añadir nueva guía
            </SecondaryButton>
            {creationDisabledReason && (
              <p className="text-xs text-gray-500 leading-4 w-44 text-right">
                {creationDisabledReason}
              </p>
            )}
          </div>

          <div>
            <PrimaryButton
              disabled={loading}
              onClick={handleSubmit}
              className="flex w-36"
            >
              CONTINUAR
            </PrimaryButton>
          </div>
        </div>
      </div>

      <Modal openModal={openDiscardAll} setOpenModal={setOpenDiscardAll}>
        <div className="flex flex-col gap-4 max-w-[25rem] items-center">
          <h2 className="text-2xl font-semibold leading-7 text-gray-900">
            Descartar cambios
          </h2>

          <div className="flex flex-col items-center justify-center w-full">
            <ExclamationTriangleIcon className="w-32 h-32 text-indigo-600" />
          </div>

          <div className="text-center mb-3">
            <p>¿Está seguro que desea descartar todos los cambios?</p>
            <p>Se perderán los cambios realizados a las guías: </p>
            <p className="font-semibold">
              {shipmentDrafts
                .filter((s) => !!s.shipmentNumber)
                .map((s) => s.shipmentNumber)
                .join(", ")}
            </p>
          </div>

          <div className="flex flex-row gap-4 items-center justify-between w-full">
            <SecondaryButton
              onClick={() => setOpenDiscardAll(false)}
              className="w-32"
            >
              Cancelar
            </SecondaryButton>

            <PrimaryButton onClick={handleDiscardAll} className="w-32">
              Descartar todo
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ShipmentFormList;
